import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, LogOut, Loader2, Users, Power, RefreshCw } from "lucide-react";
import { api, setAuthToken } from "@/lib/api";
import { useLaunch } from "@/context/LaunchContext";
import { shorten } from "@/lib/chains";

const TOKEN_KEY = "cw_admin_token";

export default function Admin() {
  const nav = useNavigate();
  const { refresh } = useLaunch();
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const [users, setUsers] = useState([]);
  const [launchOn, setLaunchOn] = useState(true);
  const [loadingData, setLoadingData] = useState(false);

  const loadData = useCallback(async () => {
    setLoadingData(true);
    try {
      const [{ data: us }, { data: lc }] = await Promise.all([
        api.get("/admin/users"),
        api.get("/config/launch"),
      ]);
      setUsers(us || []);
      setLaunchOn(!!lc.enabled);
    } catch {
      // ignore
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setChecking(false); return; }
    setAuthToken(token);
    api.get("/admin/me")
      .then(() => { setAuthed(true); loadData(); })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setAuthToken(null); })
      .finally(() => setChecking(false));
  }, [loadData]);

  const login = async () => {
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/admin/login", form);
      localStorage.setItem(TOKEN_KEY, data.token);
      setAuthToken(data.token);
      setAuthed(true);
      loadData();
    } catch (e) {
      setErr(e.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthToken(null);
    setAuthed(false);
  };

  const toggleLaunch = async () => {
    const next = !launchOn;
    setLaunchOn(next);
    try {
      await api.put("/admin/launch", { enabled: next });
      await refresh();
      toast.success(next ? "App launched — live" : "App locked — Launching Soon");
    } catch {
      setLaunchOn(!next);
      toast.error("Failed to update launch state");
    }
  };

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-neon" /></div>;
  }

  // ---------- Login ----------
  if (!authed) {
    return (
      <div className="cw-shell min-h-screen items-center justify-center px-6">
        <div className="w-full fade-up">
          <div className="mb-8 flex flex-col items-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-neon/40 bg-neon/5 neon-glow">
              <ShieldCheck className="h-8 w-8 text-neon" />
            </div>
            <h1 className="mt-5 font-outfit text-2xl font-extrabold">Admin Gateway</h1>
            <p className="mt-1 font-sans text-sm text-[#9CA3AF]">Restricted — JWT authentication</p>
          </div>

          <label className="mb-1.5 block font-sans text-xs text-[#9CA3AF]">Username</label>
          <input
            data-testid="admin-email-input"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className="mb-4 w-full rounded-xl border border-[#1F2430] bg-[#12141A] px-4 py-3 font-rajdhani text-sm text-white outline-none focus:border-neon/50"
          />
          <label className="mb-1.5 block font-sans text-xs text-[#9CA3AF]">Password</label>
          <input
            data-testid="admin-password-input"
            type="password"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && login()}
            className="w-full rounded-xl border border-[#1F2430] bg-[#12141A] px-4 py-3 font-rajdhani text-sm text-white outline-none focus:border-neon/50"
          />
          {err && <p data-testid="admin-login-error" className="mt-3 font-sans text-sm text-[#EF4444]">{err}</p>}

          <button
            data-testid="admin-login-submit-button"
            onClick={login}
            disabled={loading}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-neon py-4 font-outfit font-bold text-[#08090C] disabled:opacity-50 active:scale-[0.98] neon-glow"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null} Authenticate
          </button>
          <button onClick={() => nav("/")} className="mt-4 w-full py-2 text-center font-sans text-xs text-[#6B7280] hover:text-white">← Back to wallet</button>
        </div>
      </div>
    );
  }

  // ---------- Dashboard ----------
  return (
    <div className="cw-shell min-h-screen px-5 py-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-neon" />
          <h1 className="font-outfit text-xl font-extrabold">Admin Console</h1>
        </div>
        <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-1.5 rounded-full border border-[#1F2430] px-3 py-1.5 font-sans text-xs text-[#9CA3AF] hover:text-white">
          <LogOut className="h-4 w-4" /> Logout
        </button>
      </div>

      {/* launch control */}
      <div className={`card-surface p-5 ${launchOn ? "" : "border-[#F59E0B]/40"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Power className={`h-6 w-6 ${launchOn ? "text-[#10B981]" : "text-[#F59E0B]"}`} />
            <div>
              <p className="font-outfit text-base font-bold text-white">App Launch</p>
              <p className="font-sans text-xs text-[#9CA3AF]">
                {launchOn ? "Live — wallet accessible to everyone" : "Locked — showing Launching Soon"}
              </p>
            </div>
          </div>
          <button
            data-testid="admin-app-launch-toggle-switch"
            role="switch"
            aria-checked={launchOn}
            onClick={toggleLaunch}
            className={`relative h-9 w-16 rounded-full transition-colors ${launchOn ? "bg-neon neon-glow" : "bg-[#2A2F3D]"}`}
          >
            <span className={`absolute top-1 h-7 w-7 rounded-full bg-[#08090C] transition-all ${launchOn ? "left-8" : "left-1"}`} />
          </button>
        </div>
      </div>

      {/* user directory */}
      <div className="mb-3 mt-7 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-[#9CA3AF]" />
          <p className="font-outfit text-sm font-semibold text-[#9CA3AF]">User Directory ({users.length})</p>
        </div>
        <button onClick={loadData} className="text-[#6B7280] hover:text-neon">
          <RefreshCw className={`h-4 w-4 ${loadingData ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="overflow-hidden card-surface">
        <table data-testid="admin-user-directory-table" className="w-full text-left">
          <thead>
            <tr className="border-b border-[#1F2430] font-sans text-[10px] uppercase tracking-wider text-[#6B7280]">
              <th className="px-3 py-3">Name</th>
              <th className="px-3 py-3">Email</th>
              <th className="px-3 py-3">TRC20</th>
              <th className="px-3 py-3 text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan={4} className="px-3 py-8 text-center font-sans text-xs text-[#6B7280]">No users registered yet</td></tr>
            ) : users.map((u) => (
              <tr key={u.id} data-testid="admin-user-row" className="border-b border-[#1F2430]/60 last:border-0">
                <td className="px-3 py-3 font-outfit text-xs font-semibold text-white">{u.name}</td>
                <td className="px-3 py-3 font-sans text-xs text-[#9CA3AF]">{u.email}</td>
                <td className="px-3 py-3 font-rajdhani text-xs text-[#9CA3AF]">{shorten(u.tron_address, 6)}</td>
                <td className="px-3 py-3 text-right font-rajdhani text-xs font-semibold text-neon">{u.balance} TRX</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="h-4" />
    </div>
  );
}
